import { useEffect, useRef, useState } from 'react';

/** Re-poll cadence for the API-reachability heartbeat (ms). Independent of the map
 *  bbox — a dead API is dead at any zoom. */
const POLL_MS = 25_000;
/** ARM the unreachable state only after this many CONSECUTIVE hard failures — a one-off
 *  500 or a transient network blip must NOT raise the banner (honest, not jumpy). */
const FAIL_THRESHOLD = 3;

export interface ApiHealthView {
  /** False ONLY after ≥3 consecutive HARD failures (network error or HTTP ≥500).
   *  Clears (→ true) on the FIRST success — asymmetric, so a recovered API drops the
   *  banner immediately. Starts true (optimistic; the banner never flashes on boot). */
  reachable: boolean;
  /** Browser-clock epoch ms of the last CONFIRMED reach, or null if never confirmed
   *  this session. Age is judged against the browser clock at BOTH ends — there is no
   *  server clock to trust during an outage, so this avoids any skew to chase. */
  lastReachedAt: number | null;
}

/**
 * Honest API-reachability surfacing (#325). Polls `GET /api/health` every ~25s and
 * arms an "unreachable" state after ≥3 consecutive HARD failures — a network error
 * (total outage) or an HTTP ≥500 (the server itself broke). A response of ANY status
 * below 500 (INCLUDING a 401) proves the API answered, so it counts as reachable and
 * resets the streak: a 401 is the AUTH path, never an outage, and must not raise the
 * banner.
 *
 * The point (#325): a total /api outage otherwise renders as a clean quiet board — no
 * alerts, rail badge "0" — a dangerous FALSE all-clear. This hook lets the COP say
 * "cannot confirm" instead of implying "all safe".
 *
 * Uses a DIRECT `fetch` (NOT api.ts / authedFetch) so it never triggers the 401 →
 * logout handler and stays a pure reachability probe. It reads only the response
 * STATUS (never the body), so a non-JSON error page can't crash the probe.
 */
export function useApiHealth(): ApiHealthView {
  const [reachable, setReachable] = useState(true);
  const [lastReachedAt, setLastReachedAt] = useState<number | null>(null);
  const failCount = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const ctl = new AbortController();

    const probe = async (): Promise<void> => {
      let failed = false;
      try {
        const res = await fetch('/api/health', { cache: 'no-store', signal: ctl.signal });
        // Only a 5xx (the server itself broke) is a hard failure. A 401/403/404 means
        // the API IS reachable (it answered) — the auth/route layer, not an outage.
        if (res.status >= 500) failed = true;
      } catch {
        if (ctl.signal.aborted) return; // an unmount abort is not an outage
        failed = true; // network error → total outage
      }
      if (cancelled || ctl.signal.aborted) return;
      if (failed) {
        failCount.current += 1;
        if (failCount.current >= FAIL_THRESHOLD) setReachable(false);
      } else {
        failCount.current = 0;
        setReachable(true);
        setLastReachedAt(Date.now());
      }
    };

    void probe();
    const id = setInterval(() => void probe(), POLL_MS);
    return () => {
      cancelled = true;
      ctl.abort();
      clearInterval(id);
    };
  }, []);

  return { reachable, lastReachedAt };
}
